import { User } from '@shared/api/user/user.interface';
import { UserRole } from '@shared/types';

/**
 * 답례품 대리주문(상주검색 → 관리자/마리가 상주 대신 주문) 컨텍스트.
 *
 * 관리자(Admin/GiftAgent)가 상주검색 페이지에서 "대리주문"으로 스토어에 진입하면
 * localStorage에 이 컨텍스트를 남긴다. 스토어 쪽에서는 이 컨텍스트로
 * - 발인+30일 온라인 주문 마감 게이트를 우회하고 (전화 주문 대응이 목적)
 * - 체크아웃 주문자(상주) 이름·연락처를 미리 채우고
 * - 결제 직전 관리자 세션을 보관한 뒤 상주 명의 게스트로 전환한다.
 *   (주문·가상계좌 입금안내 LMS·이후 상주 본인의 이름+번호 로그인이 모두
 *    상주 게스트 계정에 귀속되게 하기 위함)
 *
 * localStorage를 쓰는 이유: 새 탭에서 스토어를 열어도 공유되어야 하고,
 * Bootpay 결제창 왕복(리로드) 후에도 유지되어야 한다.
 */

const ORDER_KEY = 'giftAgentOrder';
const ADMIN_SESSION_KEY = 'giftAgentOrder.adminSession';

// 이 시간이 지나면 대리주문 모드로 보지 않는다. (보관된 관리자 세션은 별도 키라
// 만료돼도 '관리자 복귀'는 가능)
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface GiftAgentOrderContext {
  bugoId: string;
  memberId: string;
  memberName: string;
  memberPhone: string;
  //* 상가 단위 주문 이력 패널용 (구버전 컨텍스트엔 없을 수 있어 옵셔널)
  feventId?: string;
  //* 배너에 어느 상가인지 함께 띄우기 위한 표시용 정보 (구버전 컨텍스트엔 없음)
  deceasedName?: string;
  //* 상주가 고인을 부르는 관계 (자·며느리 …)
  relationType?: string;
  //* 고인 성별까지 반영한 역관계 (부친·시모 …) — 상주 기준 "○○상"
  reverseType?: string;
  startedAt: number;
}

interface StashedAdminSession {
  token: string;
  userProfile: string | null;
  userBugoBrand: string | null;
}

// 직전 대리주문의 상주 — "종료" 후에도 남겨, 다음 대리주문이 같은 상주인지 판단.
// 다른 상주면 이전 주문의 입력 잔재(감사메시지·배송지 localStorage 보존분)를
// 비워야 명의가 섞이는 실수를 막는다. (같은 상주 재주문이면 유지가 오히려 유용)
const LAST_MEMBER_KEY = 'giftAgentOrder.lastMemberId';

/** 직전 대리주문과 같은 상주인지. 이전 기록이 없으면 false(=잔재 초기화 대상). */
export const isSameMemberAsLastAgentOrder = (memberId: string): boolean =>
  localStorage.getItem(LAST_MEMBER_KEY) === memberId;

export const startGiftAgentOrder = (
  ctx: Omit<GiftAgentOrderContext, 'startedAt'>,
): void => {
  const value: GiftAgentOrderContext = { ...ctx, startedAt: Date.now() };
  localStorage.setItem(ORDER_KEY, JSON.stringify(value));
  localStorage.setItem(LAST_MEMBER_KEY, ctx.memberId);
};

/** 활성 대리주문 컨텍스트. bugoId를 주면 해당 부고와 일치할 때만 반환한다. */
export const getGiftAgentOrder = (bugoId?: string): GiftAgentOrderContext | null => {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    if (!raw) return null;
    const ctx = JSON.parse(raw) as GiftAgentOrderContext;
    if (!ctx?.bugoId || !ctx?.memberName || !ctx?.memberPhone) return null;
    if (Date.now() - (ctx.startedAt ?? 0) > MAX_AGE_MS) return null;
    if (bugoId && ctx.bugoId !== bugoId) return null;
    return ctx;
  } catch {
    return null;
  }
};

export const clearGiftAgentOrder = (): void => {
  localStorage.removeItem(ORDER_KEY);
};

/**
 * 현재 localStorage의 관리자(Admin/GiftAgent) 세션을 보관한다.
 * 게스트 전환(signupOrLoginStoreGuest)이 token/userProfile을 덮어쓰기 직전에 호출.
 * - 관리자 세션이 아니면(이미 게스트로 전환된 재시도 등) 아무것도 하지 않는다.
 * - 이미 보관분이 있으면 덮어쓰지 않는다. (연속 대리주문 중 게스트 토큰이
 *   관리자 세션 자리를 차지하는 사고 방지)
 */
export const stashAdminSessionForAgentOrder = (): void => {
  try {
    if (localStorage.getItem(ADMIN_SESSION_KEY)) return;
    const token = localStorage.getItem('token');
    const userProfile = localStorage.getItem('userProfile');
    if (!token || !userProfile) return;
    const roles = (JSON.parse(userProfile) as User).roles ?? [];
    const isAdminSession =
      roles.includes(UserRole.Admin) || roles.includes(UserRole.GiftAgent);
    if (!isAdminSession) return;
    const stash: StashedAdminSession = {
      token,
      userProfile,
      userBugoBrand: localStorage.getItem('userBugoBrand'),
    };
    localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(stash));
  } catch (err) {
    console.error(err);
  }
};

export const hasStashedAdminSession = (): boolean =>
  Boolean(localStorage.getItem(ADMIN_SESSION_KEY));

/**
 * 보관해 둔 관리자 세션을 localStorage에 되돌리고 보관분·대리주문 컨텍스트를 지운다.
 * recoil atom들은 메모리에 게스트 세션을 들고 있으므로 호출 후 반드시
 * window.location 전체 리로드로 재초기화해야 한다.
 * @returns 복원 여부 (보관분이 없으면 false — 게스트 전환 전이라 복원 불필요)
 */
export const restoreAdminSessionFromAgentOrder = (): boolean => {
  clearGiftAgentOrder();
  try {
    const raw = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!raw) return false;
    const stash = JSON.parse(raw) as StashedAdminSession;
    if (!stash?.token) return false;
    localStorage.setItem('token', stash.token);
    if (stash.userProfile) localStorage.setItem('userProfile', stash.userProfile);
    else localStorage.removeItem('userProfile');
    if (stash.userBugoBrand) localStorage.setItem('userBugoBrand', stash.userBugoBrand);
    else localStorage.removeItem('userBugoBrand');
    localStorage.removeItem(ADMIN_SESSION_KEY);
    return true;
  } catch (err) {
    console.error(err);
    return false;
  }
};
